/**
 * Motor de filtrado genérico.
 *
 * Una condición es un objeto serializable:
 *   { id, field, operator, value, value2 }
 *
 * Un grupo combina condiciones con una lógica:
 *   { logic: 'AND' | 'OR', conditions: [] }
 *
 * Los operadores disponibles se derivan del tipo del campo, por lo que el
 * interfaz de filtrado se construye solo (tanto el panel global como el menú
 * de cada columna de la tabla).
 */

import { resolveField } from './fieldsNew'

/** input: forma del control que necesita el operador. */
export const OPERATORS = {
  text: [
    { value: 'contains', label: 'contiene', input: 'text' },
    { value: 'notContains', label: 'no contiene', input: 'text' },
    { value: 'equals', label: 'es igual a', input: 'text' },
    { value: 'notEquals', label: 'es distinto de', input: 'text' },
    { value: 'startsWith', label: 'empieza por', input: 'text' },
    { value: 'endsWith', label: 'termina por', input: 'text' },
    { value: 'in', label: 'es uno de', input: 'multi' },
    { value: 'isEmpty', label: 'está vacío', input: 'none' },
    { value: 'isNotEmpty', label: 'no está vacío', input: 'none' },
  ],
  enum: [
    { value: 'in', label: 'es uno de', input: 'multi' },
    { value: 'notIn', label: 'no es ninguno de', input: 'multi' },
    { value: 'equals', label: 'es igual a', input: 'select' },
    { value: 'isEmpty', label: 'está vacío', input: 'none' },
    { value: 'isNotEmpty', label: 'no está vacío', input: 'none' },
  ],
  number: [
    { value: 'eq', label: '=', input: 'number' },
    { value: 'neq', label: '≠', input: 'number' },
    { value: 'gt', label: '>', input: 'number' },
    { value: 'gte', label: '≥', input: 'number' },
    { value: 'lt', label: '<', input: 'number' },
    { value: 'lte', label: '≤', input: 'number' },
    { value: 'betweenNumber', label: 'entre', input: 'numberRange' },
    { value: 'isEmpty', label: 'está vacío', input: 'none' },
    { value: 'isNotEmpty', label: 'no está vacío', input: 'none' },
  ],
  date: [
    { value: 'dateBetween', label: 'entre', input: 'dateRange' },
    { value: 'dateFrom', label: 'desde', input: 'date' },
    { value: 'dateTo', label: 'hasta', input: 'date' },
    { value: 'dateEquals', label: 'es el', input: 'date' },
    { value: 'dateBefore', label: 'antes del', input: 'date' },
    { value: 'dateAfter', label: 'después del', input: 'date' },
    { value: 'lastDays', label: 'en los últimos (días)', input: 'number' },
    { value: 'inYear', label: 'del año', input: 'number' },
    { value: 'isEmpty', label: 'sin fecha', input: 'none' },
    { value: 'isNotEmpty', label: 'con fecha', input: 'none' },
  ],
  boolean: [
    { value: 'isTrue', label: 'es Sí', input: 'none' },
    { value: 'isFalse', label: 'es No', input: 'none' },
  ],
}
// Los campos "dateRange" (un solo control con inicio/fin) usan los mismos
// operadores que "date": sin esto, operatorsForField cae a OPERATORS.text y
// las condiciones dateFrom/dateTo que genera el filtro rápido se descartan
// por no encontrar su operador.
OPERATORS.dateRange = OPERATORS.date

export function operatorsForField(fieldKey, fields) {
  const field = resolveField(fieldKey, fields)
  if (!field) return []
  return OPERATORS[field.type] || OPERATORS.text
}

export function operatorMeta(fieldKey, operator, fields) {
  return operatorsForField(fieldKey, fields).find((op) => op.value === operator)
}

export function defaultOperator(fieldKey, fields) {
  const list = operatorsForField(fieldKey, fields)
  return list.length ? list[0].value : 'contains'
}

let conditionSeq = 0
export function createCondition(fieldKey, fields) {
  conditionSeq += 1
  const operator = defaultOperator(fieldKey, fields)
  const meta = operatorMeta(fieldKey, operator)
  return {
    id: `cond-${conditionSeq}`,
    field: fieldKey,
    operator,
    value: meta && meta.input === 'multi' ? [] : '',
    value2: '',
  }
}

const normalize = (value) =>
  String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const toNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isNaN(parsed) ? null : parsed
}

/**
 * Valor "plano" de una celda para filtrar. Si el campo declara objectProp
 * (p. ej. tipoResiduo con { codTipoResiduo, descripcion }), extrae esa
 * propiedad en lugar de usar el objeto entero.
 */
export function getFieldValue(row, fieldKey, fields) {
  const raw = row[fieldKey]
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const field = resolveField(fieldKey, fields)
    if (field?.objectProp && raw[field.objectProp] !== undefined) {
      return raw[field.objectProp]
    }
  }
  return raw
}

/** ¿La condición está lo bastante completa para aplicarse? */
export function isConditionActive(condition, fields) {
  if (!condition || !condition.field || !condition.operator) return false
  const meta = operatorMeta(condition.field, condition.operator, fields)
  if (!meta) return false
  switch (meta.input) {
    case 'none':
      return true
    case 'multi':
      return Array.isArray(condition.value) && condition.value.length > 0
    case 'numberRange':
    case 'dateRange':
      return condition.value !== '' || condition.value2 !== ''
    default:
      return condition.value !== '' && condition.value !== null && condition.value !== undefined
  }
}

function matchCondition(row, condition, fields) {
  const raw = getFieldValue(row, condition.field, fields)
  const { operator, value, value2 } = condition

  switch (operator) {
    case 'contains':
      return normalize(raw).includes(normalize(value))
    case 'notContains':
      return !normalize(raw).includes(normalize(value))
    case 'equals':
      return normalize(raw) === normalize(value)
    case 'notEquals':
      return normalize(raw) !== normalize(value)
    case 'startsWith':
      return normalize(raw).startsWith(normalize(value))
    case 'endsWith':
      return normalize(raw).endsWith(normalize(value))
    case 'in':
      return (value || []).some((item) => normalize(item) === normalize(raw))
    case 'notIn':
      return !(value || []).some((item) => normalize(item) === normalize(raw))
    case 'isEmpty':
      return raw === null || raw === undefined || raw === ''
    case 'isNotEmpty':
      return !(raw === null || raw === undefined || raw === '')
    case 'isTrue':
      return raw === true
    case 'isFalse':
      return raw === false

    case 'eq':
    case 'neq':
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const a = toNumber(raw)
      const b = toNumber(value)
      if (a === null || b === null) return false
      if (operator === 'eq') return a === b
      if (operator === 'neq') return a !== b
      if (operator === 'gt') return a > b
      if (operator === 'gte') return a >= b
      if (operator === 'lt') return a < b
      return a <= b
    }
    case 'betweenNumber': {
      const a = toNumber(raw)
      if (a === null) return false
      const min = toNumber(value)
      const max = toNumber(value2)
      if (min !== null && a < min) return false
      if (max !== null && a > max) return false
      return true
    }

    case 'dateEquals':
      return String(raw || '').slice(0, 10) === String(value)
    case 'dateBefore':
      return Boolean(raw) && String(raw).slice(0, 10) < String(value)
    case 'dateAfter':
      return Boolean(raw) && String(raw).slice(0, 10) > String(value)
    case 'dateFrom':
      return Boolean(raw) && String(raw).slice(0, 10) >= String(value)
    case 'dateTo':
      return Boolean(raw) && String(raw).slice(0, 10) <= String(value)
    case 'dateBetween': {
      if (!raw) return false
      const day = String(raw).slice(0, 10)
      if (value && day < String(value)) return false
      if (value2 && day > String(value2)) return false
      return true
    }
    case 'lastDays': {
      const days = toNumber(value)
      if (!raw || days === null) return false
      const limit = new Date()
      limit.setDate(limit.getDate() - days)
      return new Date(String(raw)) >= limit
    }
    case 'inYear': {
      const year = toNumber(value)
      if (!raw || year === null) return false
      return Number(String(raw).slice(0, 4)) === year
    }

    default:
      return true
  }
}

/** Aplica un grupo de condiciones (AND/OR) sobre un array de filas. */
export function applyFilterGroup(rows, group, fields) {
  if (!group) return rows
  const active = (group.conditions || []).filter((condition) =>
    isConditionActive(condition, fields)
  )
  if (!active.length) return rows

  if (group.logic === 'OR') {
    return rows.filter((row) =>
      active.some((condition) => matchCondition(row, condition, fields))
    )
  }
  return rows.filter((row) =>
    active.every((condition) => matchCondition(row, condition, fields))
  )
}

/** Aplica varios grupos, siempre en AND entre ellos (global + columna + widget). */
export function applyFilterGroups(rows, groups, fields) {
  return groups
    .filter(Boolean)
    .reduce((acc, group) => applyFilterGroup(acc, group, fields), rows)
}

/** Búsqueda libre sobre los campos indicados (o todos los de texto/enum). */
export function applyQuickSearch(rows, query, keys, fields) {
  const term = normalize(query).trim()
  if (!term) return rows
  return rows.filter((row) =>
    keys.some((key) => normalize(getFieldValue(row, key, fields)).includes(term))
  )
}

export function countActiveConditions(group, fields) {
  if (!group) return 0
  return (group.conditions || []).filter(
    (condition) => isConditionActive(condition, fields)
  ).length
}

/** Texto legible de una condición (para los chips de filtros activos). */
export function describeCondition(condition, fields) {
  const field = resolveField(condition.field, fields)
  const meta = operatorMeta(condition.field, condition.operator, fields)
  if (!field || !meta) return ''
  const label = `${field.label} ${meta.label}`

  switch (meta.input) {
    case 'none':
      return label
    case 'multi': {
      const values = condition.value || []
      const shown = values.slice(0, 2).join(', ')
      return `${label} ${shown}${values.length > 2 ? ` +${values.length - 2}` : ''}`
    }
    case 'numberRange':
    case 'dateRange':
      return `${label} ${condition.value || '−∞'} y ${condition.value2 || '+∞'}`
    default:
      return `${label} ${condition.value}`
  }
}
